import React, { useState, useEffect } from "react";
import { Button, Card, Spin, Input, message, Alert } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import logoPng from "../static/logo.png";
import { AuthService } from "../services/auth.service";

interface LoginProps {
  onLoginSuccess: () => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>("");
  const [quickLoginId, setQuickLoginId] = useState<string>("");
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();
  const location = useLocation();
  const isProduction = process.env.NODE_ENV === "production";

  // 获取URL中的错误信息
  const urlParams = new URLSearchParams(location.search);
  const errorFromUrl = urlParams.get("error");

  // 处理URL中的错误消息
  useEffect(() => {
    if (errorFromUrl) {
      setError(errorFromUrl);
      // 清除URL中的错误参数
      navigate("/login", { replace: true });
    }
  }, [errorFromUrl, navigate]);

  useEffect(() => {
    // 获取微信登录二维码
    const fetchWechatQRCode = () => {
      try {
        setLoading(true);
        setError(""); // 清除之前的错误

        // 微信公众号开放平台的AppID，需要在环境变量中配置
        const appId = process.env.REACT_APP_WECHAT_OAUTH_APPID || "";
        if (!appId) {
          throw new Error("微信OAuth AppID未配置");
        }

        // 授权后的回调页面配置
        // 支持环境变量配置，如果未配置则使用默认值
        const baseUrl =
          process.env.REACT_APP_BASE_URL || window.location.origin;

        const callbackPath = "/wechat-callback";

        const fullRedirectUri = `${baseUrl}${callbackPath}`;

        const redirectUri = encodeURIComponent(fullRedirectUri);

        // 随机状态码，防止CSRF攻击
        const state = Math.random().toString(36).substring(2, 15);
        // 保存state到localStorage，回调时验证
        localStorage.setItem("wechat_auth_state", state);

        const qrCodeLink = `https://open.weixin.qq.com/connect/qrconnect?appid=${appId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_login&state=${state}#wechat_redirect`;

        console.log("qrCodeLink", qrCodeLink);

        setQrCodeUrl(qrCodeLink);
        setLoading(false);
      } catch (err) {
        console.error("生成微信登录二维码失败:", err);

        // 提供更详细的错误信息
        let errorMessage = "生成微信登录二维码失败";
        if (err instanceof Error) {
          errorMessage = err.message;
        }

        // 添加常见问题的提示
        errorMessage += "\n\n常见解决方案：\n";
        errorMessage += "1. 检查微信开放平台是否配置了正确的授权回调域名\n";
        errorMessage += "2. 确认域名协议（HTTP/HTTPS）与配置一致\n";
        errorMessage += "3. 验证 AppID 是否正确配置";

        setError(errorMessage);
        setLoading(false);
      }
    };

    fetchWechatQRCode();
  }, []);

  // 检查URL中是否包含code参数（微信回调）
  // useEffect(() => {
  //   const urlParams = new URLSearchParams(window.location.search);
  //   const code = urlParams.get("code");
  //   const state = urlParams.get("state");

  //   if (code) {
  //     // 验证state参数防止CSRF攻击
  //     const savedState = localStorage.getItem("wechat_auth_state");
  //     if (state && savedState && state === savedState) {
  //       // 重定向到回调页面处理登录
  //       navigate(`/wechat-callback?code=${code}&state=${state}`);
  //     } else {
  //       setError("登录状态验证失败，请重新扫码登录");
  //       // 清理localStorage
  //       localStorage.removeItem("wechat_auth_state");
  //     }
  //   }
  // }, [navigate]);

  const handleQuickLogin = async () => {
    if (!quickLoginId) {
      message.error("请输入管理员UnionID");
      return;
    }

    try {
      setLoading(true);
      const authService = AuthService.getInstance();

      // 使用 authService 的 quickLogin 方法，确保状态正确更新
      const result = await authService.quickLogin(quickLoginId);

      if (result.success) {
        message.success("登录成功");
        onLoginSuccess();
        navigate("/dashboard");
      } else {
        message.error(result.message || "登录失败");
      }
    } catch (error: any) {
      console.error("快速登录失败:", error);
      const errorMessage =
        error.message || "快速登录失败，请检查UnionID是否正确";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "20px",
        }}
      >
        <Card
          style={{
            width: 400,
            textAlign: "center",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "24px",
            }}
          >
            <img
              src={logoPng}
              alt="安安养老LOGO"
              style={{
                width: 48,
                height: 48,
                marginRight: 12,
              }}
            />
            <div
              style={{
                fontSize: "24px",
                fontWeight: "bold",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              安安养老管理后台
            </div>
          </div>

          {error && (
            <Alert
              message="登录错误"
              description={error}
              type="error"
              showIcon
              style={{ marginBottom: "16px" }}
            />
          )}

          <div style={{ marginBottom: "16px" }}>
            {loading ? (
              <Spin tip="加载中...">
                <div
                  style={{
                    height: "200px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed #f0f0f0",
                    borderRadius: "8px",
                  }}
                />
              </Spin>
            ) : qrCodeUrl ? (
              (() => {
                // 检测是否在微信浏览器中
                const isWechatBrowser = /MicroMessenger/i.test(
                  navigator.userAgent
                );

                if (isWechatBrowser) {
                  return (
                    <div
                      style={{
                        padding: "20px",
                        background: "#fff2e8",
                        border: "1px solid #ffbb96",
                        borderRadius: "8px",
                        color: "#d4380d",
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          marginBottom: "8px",
                          fontWeight: "bold",
                        }}
                      >
                        检测到您正在使用微信浏览器
                      </p>
                      <p style={{ margin: 0, fontSize: "14px" }}>
                        请点击右上角「···」菜单，
                        <br />
                        选择<strong>「在浏览器中打开」</strong>
                        <br />
                        以完成管理员登录
                      </p>
                    </div>
                  );
                }

                return (
                  <div>
                    <iframe
                      src={qrCodeUrl}
                      style={{
                        width: "300px",
                        height: "400px",
                        border: "none",
                        borderRadius: "8px",
                      }}
                      title="微信登录二维码"
                    />
                  </div>
                );
              })()
            ) : (
              <div
                style={{
                  padding: "40px",
                  border: "2px dashed #ff4d4f",
                  borderRadius: "8px",
                  color: "#ff4d4f",
                }}
              >
                <p style={{ margin: "0 0 16px 0" }}>获取登录二维码失败</p>
                <Button
                  type="primary"
                  danger
                  onClick={() => window.location.reload()}
                >
                  刷新页面
                </Button>
              </div>
            )}
          </div>
        </Card>

        {!isProduction && (
          <Card
            title="开发环境快速登录"
            style={{
              width: 400,
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Input
              placeholder="输入管理员UnionID"
              value={quickLoginId}
              onChange={(e) => setQuickLoginId(e.target.value)}
              style={{ marginBottom: 16 }}
              onPressEnter={handleQuickLogin}
            />
            <Button
              type="primary"
              onClick={handleQuickLogin}
              loading={loading}
              block
            >
              测试登录
            </Button>
            <div
              style={{
                marginTop: "12px",
                fontSize: "12px",
                color: "#666",
                textAlign: "center",
              }}
            >
              开发环境可直接使用UnionID登录
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Login;
